/**
 * Where "View on the web" lands: trades the one-time code from the desktop app for a session in
 * this browser, then goes to the screen the person was on. See AuthHandoff on the server.
 *
 * A code says nothing about who clicked it: anyone can make one for their own account and send the
 * link, and a browser that took it silently would end up in the sender's organization, typing its
 * prompts there (security sweep, 12/10/2026). So the account is shown and the person confirms, unless
 * this browser is already signed in as that same account.
 *
 * The code comes after the `#` (SEC2-03, docs/refactor/54): that part of an address never leaves the browser — no
 * server, proxy or Referer sees it. `?code=` still works, for desktop apps from before 0.117.0.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { getSession, saveSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import Logo from '../components/Logo'
import { safeNext } from '../lib/safeNext'
import type { AuthResult } from '../lib/types'

export default function Handoff() {
  const { t } = useI18n()
  const [query] = useSearchParams()
  const { hash } = useLocation()
  const fragment = new URLSearchParams(hash.replace(/^#/, ''))
  const params = { get: (name: string) => fragment.get(name) ?? query.get(name) }
  const [failed, setFailed] = useState(false)
  const [pending, setPending] = useState<AuthResult | null>(null)
  const started = useRef(false)
  const next = safeNext(params.get('next'))

  const enter = (result: AuthResult) => {
    saveSession(result)
    // A fresh load rather than navigate(): this page is rendered by the signed-out routes,
    // which would redirect the target to /sign-in before the signed-in ones take over.
    window.location.replace(next)
  }

  useEffect(() => {
    // Once: a code is good for one try, and React may run this twice in development.
    if (started.current) return
    started.current = true
    const code = params.get('code')
    // The code leaves the address bar and the history straight away.
    window.history.replaceState(null, '', '/auth/handoff')
    api.auth
      .redeemHandoff(code ?? '')
      .then((result) => {
        if (getSession()?.userId === result.userId) enter(result)
        else setPending(result)
      })
      .catch(() => setFailed(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="auth">
      <div className="auth__card stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <Logo size={36} />
        {failed ? (
          <>
            <p className="small">{t('handoff.failed')}</p>
            <Link className="btn btn--primary" to="/sign-in">{t('handoff.signIn')}</Link>
          </>
        ) : pending ? (
          <>
            <p className="small">{t('handoff.confirm', { email: pending.email, organization: pending.organizationName })}</p>
            <p className="tiny muted">{t('handoff.confirmHint')}</p>
            <div className="row row--tight">
              <Link className="btn btn--ghost" to="/sign-in">{t('handoff.notMe')}</Link>
              <button type="button" className="btn btn--primary" onClick={() => enter(pending)}>
                {t('handoff.continue')}
              </button>
            </div>
          </>
        ) : (
          <p className="small muted">{t('handoff.opening')}</p>
        )}
      </div>
    </div>
  )
}
